import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes"

// Tracks a form for unsaved changes and blocks guard forms (e.g. "Send Test Email") from submitting when dirty.
// This controller must wrap both the form and the guard form(s).
// Guard target is always the form (the element that fires submit), not the submit button.
// For a single form, use both targets on the same form: data-unsaved-changes-target="form guard".
export default class extends Controller {
  static targets = ["form", "guard"]
  static values = {
    message: { type: String, default: "You have unsaved changes. Please save and try again." },
  }

  // Sets up the controller by:
  // - Getting the initial state of the form
  // - Setting up the dirty flag
  // - Binding the checkDirty and handleGuardSubmit methods
  // - Adding event listeners to the tracked elements
  // - Adding event listeners to the guard form
  connect() {
    if (!this.hasFormTarget) return

    this.initialState = this.getFormState(this.formTarget)
    this.dirty = false
    this._turboConfirmShown = false
    this.boundCheckDirty = this.checkDirty.bind(this)
    this.boundHandleGuardSubmit = this.handleGuardSubmit.bind(this)
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)
    this.trackedElements = this.getTrackedElements(this.formTarget)
    this.boundHandleTurboBeforeVisit = this.handleTurboBeforeVisit.bind(this)
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
  
    this.trackedElements.forEach((el) => {
      el.addEventListener("input", this.boundCheckDirty)
      el.addEventListener("change", this.boundCheckDirty)
    })

    this.formTarget.addEventListener("submit", this.boundHandleFormSubmit)

    if (this.hasGuardTarget) {
      this.guardTargets.forEach((guard) => {
        guard.addEventListener("submit", this.boundHandleGuardSubmit)
      })
    }
    // listen for link navigation to notify user of unsaved changes
    document.addEventListener("turbo:before-visit", this.boundHandleTurboBeforeVisit)
    // listen for page refresh, tab close, or navigating away to notify user of unsaved changes
    window.addEventListener("beforeunload", this.boundHandleBeforeUnload)
  }

  // Cleans up the controller by:
  // - Removing global listeners from window/document (always — they were added
  //   unconditionally if connect ran, and Stimulus only calls disconnect after
  //   a successful connect, so the bound references exist).
  // - Removing per-element listeners only if the targets are still resolvable.
  disconnect() {
    if (this.boundHandleTurboBeforeVisit) {
      document.removeEventListener("turbo:before-visit", this.boundHandleTurboBeforeVisit)
    }
    if (this.boundHandleBeforeUnload) {
      window.removeEventListener("beforeunload", this.boundHandleBeforeUnload)
    }

    if (!this.hasFormTarget) return

    this.formTarget.removeEventListener("submit", this.boundHandleFormSubmit)

    if (this.trackedElements) {
      this.trackedElements.forEach((el) => {
        el.removeEventListener("input", this.boundCheckDirty)
        el.removeEventListener("change", this.boundCheckDirty)
      })
    }

    if (this.hasGuardTarget) {
      this.guardTargets.forEach((guard) => {
        guard.removeEventListener("submit", this.boundHandleGuardSubmit)
      })
    }
  }

  // Form elements to track (excludes submit, button, hidden, disabled).
  // includes disabled password fields to keep tracking when a secret field is later enabled
  // Returns array of elements to track: [input, select, textarea].
  getTrackedElements(form) {
    const selector =
      "input:not([type=submit]):not([type=button]):not([type=hidden]):not([disabled]),input[type=password], select:not([disabled]), textarea:not([disabled])"
    return Array.from(form.querySelectorAll(selector))
  }

  // Snapshot of form values for dirty comparison (handles checkbox, radio, multi-select).
  // Returns object: { key: value, key: value, ... } (values are string or boolean for checkbox).
  // Password fields are stored as sentinels (__EMPTY__ / __HAS_VALUE__) only; actual values are never stored.
  getFormState(form) {
    const state = {}
    const elements = this.getTrackedElements(form)

    elements.forEach((el) => {
      const key = this.getFieldKey(el, elements)
      state[key] = this.getFieldValue(el, form)
    })

    return state
  }

  // Creates a consistent key for a field (radios by name, others by id or name + index).
  // Returns string key for the field; fallback "field" if no name. index for uniqueness.
  getFieldKey(el, allElements) {
    if (el.type === "radio") return el.name
  
    const index = allElements.indexOf(el)
    return el.id || `${el.name || "field"}__${index}`
  }

  // Current field value for comparison (checkbox/radio/select handled).
  // Returns boolean for checkbox; string for radio, single-select, and text; comma-separated string for multi-select.
  getFieldValue(el, form) {
    if (el.type === "checkbox") return el.checked

    if (el.type === "password") {
      return el.value.length > 0 ? "__HAS_VALUE__" : "__EMPTY__"
    }
  
    if (el.type === "radio") {
      const checked = form.querySelector(`input[name="${el.name}"]:checked`)
      return checked ? checked.value : ""
    }

    if (el.tagName === "SELECT") {
      if (el.multiple) {
        return Array.from(el.selectedOptions)
          .map((o) => o.value)
          .sort()
          .join(",")
      }
      return el.value
    }
  
    return el.value
  }

  // Compares initial state to current state (used for dirty check).
  // Returns true if both state objects match, else false.
  statesEqual(a, b) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const key of keys) {
      if (String(a[key] ?? "") !== String(b[key] ?? "")) return false
    }
  
    return true
  }

  // Reevaluates dirty status by comparing current form state to initial snapshot.
  // Sets this.dirty (no return).
  checkDirty() {
    if (!this.hasFormTarget) return
  
    const current = this.getFormState(this.formTarget)
    this.dirty = !this.statesEqual(this.initialState, current)
  }

  // Resets dirty flag and rebases initialState to the just-submitted values.
  // The on-demand checkDirty() in the navigation handlers below would otherwise
  // re-flag the form as dirty during turbo:before-visit (the form is still in
  // the DOM with the user's edits when Turbo fires the visit), causing a false
  // "Leave without saving?" prompt on a legitimate save.
  handleFormSubmit() {
    if (!this.hasFormTarget) return

    this.dirty = false
    this.initialState = this.getFormState(this.formTarget)
  }

  // Handles beforeunload event to prevent unsaved changes.
  // Do not block when session timeout (or similar) is performing sign-out.
  // We use localStorage.forceLogout (set by session-timeout controller
  // right before navigating).
  // Recompute dirty up-front so that programmatic field changes from other
  // controllers (e.g. secret-field#cancel clearing a disabled input without
  // firing input/change) cannot leave us with a stale flag.
  handleBeforeUnload(event) {
    this.checkDirty()
    if (!this.dirty) return
    if (localStorage.getItem("forceLogout")) return

    event.preventDefault()
    event.returnValue = ""  // required in some browsers to show the dialog
  }

  // Handles Turbo beforeVisit event to prevent unsaved changes.
  // Guard so we only show one confirm if Turbo fires the event twice (e.g. cache/preview).
  handleTurboBeforeVisit(event) {
    this.checkDirty()
    if (!this.dirty) return
    if (localStorage.getItem("forceLogout")) return

    if (this._turboConfirmShown) {
      event.preventDefault()
      return
    }

    this._turboConfirmShown = true
    const message = "You have unsaved changes. Leave without saving?"

    if (confirm(message)) {
      this.dirty = false
      this.initialState = this.getFormState(this.formTarget)
    } else {
      event.preventDefault()
    }

    setTimeout(() => { this._turboConfirmShown = false }, 0)
  }

  // Prevents submit when dirty, shows message; 
  // re-enables button after delay (Rails UJS disables it).
  handleGuardSubmit(event) {
    this.checkDirty()
    if (!this.dirty) return
  
    event.preventDefault()
    flash(this.messageValue, "error")

    setTimeout(() => {
      const form = event.target
      form.querySelectorAll('input[type=submit][disabled], button[type=submit][disabled]').forEach(el => {
        el.disabled = false
      })
    }, 500)
  }
}
